import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { cn } from "@/utils/cn";

interface SceneryProps {
    /** public 절대 경로. 없으면 아무것도 렌더하지 않음. */
    src?: string;
    /** alt 색 오버레이. 기본 vignette. */
    overlay?: "vignette" | "burgundy" | "ember" | "none";
    /** 이미지 기본 투명도. 기본 0.6 — 텍스트 가독성 우선. */
    opacity?: number;
    className?: string;
    /** 천천히 움직이는 패럴랙스. 기본 false. */
    drift?: boolean;
}

/**
 * 씬 배경 이미지 레이어. onError 시 조용히 사라짐 — 리소스 없을 때 앱 무너지지 않음.
 * 씬 루트의 <PageShell> 뒤에 절대 배치.
 */
export function Scenery({
    src,
    overlay = "vignette",
    opacity = 0.6,
    className,
    drift = false,
}: SceneryProps) {
    if (!src) return null;

    return (
        <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
            <motion.img
                src={src}
                alt=""
                initial={drift ? { scale: 1.06, opacity: 0 } : { opacity: 0 }}
                animate={drift
                    ? { scale: 1.0, opacity, x: [0, -8, 0, 8, 0], y: [0, 4, 0, -4, 0] }
                    : { opacity }
                }
                transition={drift
                    ? { opacity: { duration: 1.2 }, scale: { duration: 1.2 }, x: { duration: 40, repeat: Infinity, ease: "linear" }, y: { duration: 40, repeat: Infinity, ease: "linear" } }
                    : { duration: 1.2, ease: tokens.easing.decelerate }
                }
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-cover"
            />
            {overlay !== "none" && (
                <div
                    className="absolute inset-0"
                    style={{ background: overlayGradient(overlay) }}
                />
            )}
        </div>
    );
}

function overlayGradient(kind: "vignette" | "burgundy" | "ember"): string {
    switch (kind) {
        case "vignette":
            return `radial-gradient(ellipse at 50% 50%, transparent 30%, ${tokens.color.ink}BB 95%)`;
        case "burgundy":
            return `radial-gradient(ellipse at 50% 30%, ${tokens.color.burgundy}55 0%, ${tokens.color.ink}EE 70%)`;
        case "ember":
            return `radial-gradient(ellipse at 50% 60%, ${tokens.color.ember}25 0%, ${tokens.color.ink}EE 75%)`;
    }
}
